import React from 'react';
import { cn } from '../lib/utils';
import ClassicLoadingScreen from './Layout/classic/ClassicLoadingScreen';
import { isShellEntrancePending } from '../utils/uiPreferences';

// Mirrors the desktop app shell (inset sidebar + content card) so the swap
// from skeleton to app doesn't jump. Sidebar width follows the same persisted
// open state the shell uses — except right after sign-in, when the shell
// mounts collapsed and expands (the entrance), so the skeleton starts
// collapsed too.
const readSidebarOpen = () => {
  if (isShellEntrancePending()) return false;
  try {
    return localStorage.getItem('lodgeic.sidebar.open') !== 'false';
  } catch (_) {
    return true;
  }
};

const LoadingScreen = ({ message = 'Loading' }) => {
  // The classic shell (per-user Appearance setting, set pre-paint on <html>)
  // has its own light skeleton.
  if (document.documentElement.getAttribute('data-shell') !== 'inset') {
    return <ClassicLoadingScreen message={message} />;
  }
  const sidebarOpen = readSidebarOpen();
  const sidebarWidth = sidebarOpen ? 256 : 64;

  return (
    <div className="fixed inset-0 z-50" style={{ background: 'var(--sidebar-background, #0B0B0D)' }}>
      {/* Desktop Layout */}
      <div className="hidden md:flex h-full">
        <div className="shrink-0 flex flex-col gap-2 p-2" style={{ width: sidebarWidth }}>
          <div className={cn('flex items-center gap-2 p-2 h-12', !sidebarOpen && 'justify-center')}>
            <div className="w-8 h-8 rounded-lg bg-white/10 animate-pulse shrink-0" />
            {sidebarOpen && (
              <div className="flex-1 space-y-1.5">
                <div className="w-20 h-3 rounded bg-white/15 animate-pulse" />
                <div className="w-28 h-2.5 rounded bg-white/10 animate-pulse" />
              </div>
            )}
          </div>
          <div className="h-8 rounded-md bg-white/[0.06] animate-pulse" />
          <nav className="flex-1 space-y-1 pt-3">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={cn('flex items-center gap-2 h-8 px-2 rounded-md', i === 0 && 'bg-white/[0.08]', !sidebarOpen && 'justify-center')}>
                <div className="w-4 h-4 rounded bg-white/15 animate-pulse" />
                {sidebarOpen && <div className="h-3 rounded bg-white/10 animate-pulse" style={{ width: 70 + ((i * 23) % 60) }} />}
              </div>
            ))}
          </nav>
          <div className={cn('flex items-center gap-2 p-2 h-12', !sidebarOpen && 'justify-center')}>
            <div className="w-8 h-8 rounded-lg bg-white/10 animate-pulse shrink-0" />
            {sidebarOpen && <div className="w-32 h-3 rounded bg-white/10 animate-pulse" />}
          </div>
        </div>

        <div
          className="flex-1 m-2 flex flex-col rounded-xl shadow"
          style={{ background: 'var(--shell-card, #fff)', border: '1px solid var(--shell-card-border, transparent)', marginLeft: sidebarOpen ? 0 : 8 }}
        >
          <div className="flex items-center gap-2 px-3 border-b" style={{ height: 52, borderColor: 'var(--lodge-onyx-100)' }}>
            <div className="sk-pulse w-6 h-6 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
            <div className="sk-pulse w-40 h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
            <div className="ml-auto flex gap-2">
              <div className="sk-pulse w-48 h-8 rounded-md bg-slate-100 dark:bg-slate-800 animate-pulse" />
              <div className="sk-pulse w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-800 animate-pulse" />
              <div className="sk-pulse w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-800 animate-pulse" />
            </div>
          </div>
          {/* Generic page skeleton in the report loading language: toolbar,
              tile row, chart row. No spinner. */}
          <div className="rn-route-fallback-inset flex-1 overflow-hidden" style={{ padding: '0 16px 16px' }} role="status" aria-busy="true">
            <span className="sr-only">{message}…</span>
            <div className="rfi-toolbar" style={{ margin: 0, padding: 0 }}>
              <span className="rn-sk" style={{ width: 240, height: 26, borderRadius: 8 }} />
              <span className="rn-sk" style={{ width: 160, height: 26, borderRadius: 8 }} />
              <span className="rn-sk" style={{ width: 96, height: 26, borderRadius: 8 }} />
            </div>
            <div className="rfi-row rfi-tiles">
              {[0, 1, 2, 3, 4].map((i) => (
                <div className="rfi-card" key={i}>
                  <span className="rn-sk" style={{ width: '46%', height: 9 }} />
                  <span className="rn-sk" style={{ width: '64%', height: 24, marginTop: 12 }} />
                  <span className="rn-sk" style={{ width: '52%', height: 9, marginTop: 12 }} />
                </div>
              ))}
            </div>
            <div className="rfi-row rfi-charts">
              {[0, 1].map((i) => (
                <div className="rfi-card" key={i}>
                  <span className="rn-sk" style={{ width: '32%', height: 12 }} />
                  <span className="rn-sk" style={{ width: '100%', height: 220, marginTop: 16 }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Phone layout: mirrors Layout/mobile/MobileInsetLayout — the palette
          app bar, then the inset card with its head, toolbar, tiles, chart. */}
      <div className="md:hidden min-h-screen" role="status" aria-busy="true">
        <span className="sr-only">{message}…</span>
        <div
          className="flex items-center gap-2 px-2"
          style={{ height: 'calc(52px + env(safe-area-inset-top, 0px))', paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="w-10 h-10 flex items-center justify-center"><div className="w-5 h-4 rounded bg-white/15 animate-pulse" /></div>
          <div className="w-7 h-7 rounded-md bg-white/10 animate-pulse" />
          <div className="w-32 h-3.5 rounded bg-white/15 animate-pulse" />
          <div className="ml-auto flex gap-3 pr-2">
            <div className="w-5 h-5 rounded-full bg-white/10 animate-pulse" />
            <div className="w-5 h-5 rounded-full bg-white/10 animate-pulse" />
          </div>
        </div>
        <div
          className="flex flex-col overflow-hidden"
          style={{
            margin: '6px 6px 0',
            height: 'calc(100svh - 52px - env(safe-area-inset-top, 0px) - 12px - env(safe-area-inset-bottom, 0px))',
            borderRadius: 14,
            background: 'var(--shell-card, #fff)',
            border: '1px solid var(--shell-card-border, transparent)',
          }}
        >
          <div className="flex items-center gap-2 px-3 border-b" style={{ height: 44, borderColor: 'var(--lodge-onyx-100)' }}>
            <span className="rn-sk" style={{ width: 150, height: 12 }} />
            <span className="rn-sk ml-auto" style={{ width: 20, height: 12 }} />
          </div>
          <div className="rn-route-fallback-inset flex-1 overflow-hidden" style={{ padding: '0 12px 12px' }}>
            <div className="flex gap-2 items-center border-b" style={{ height: 48, margin: '0 -12px 12px', padding: '0 12px', borderColor: 'var(--lodge-onyx-100)' }}>
              <span className="rn-sk" style={{ width: 150, height: 30, borderRadius: 6 }} />
              <span className="rn-sk" style={{ width: 170, height: 30, borderRadius: 6 }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <div className="rfi-card" key={i}>
                  <span className="rn-sk" style={{ width: '56%', height: 9 }} />
                  <span className="rn-sk" style={{ width: '72%', height: 22, marginTop: 12 }} />
                  <span className="rn-sk" style={{ width: '48%', height: 9, marginTop: 12 }} />
                </div>
              ))}
            </div>
            <div className="rfi-card" style={{ marginTop: 8 }}>
              <span className="rn-sk" style={{ width: '38%', height: 12 }} />
              <span className="rn-sk" style={{ width: '100%', height: 200, marginTop: 16 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen; 